import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Wrench, Lock, User, Loader2, ArrowRight, ShieldCheck, Eye, EyeOff } from 'lucide-react';

export default function ProfessionalLogin() {
  const navigate = useNavigate();
  const supabase = getSupabaseClient();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // If already logged in, check if user is a service professional
  useEffect(() => {
    async function checkExistingSession() {
      if (!supabase) {
        setCheckingAuth(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Check if this user is a service professional
          const { data: sp } = await supabase
            .from('service_providers')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (sp?.id) {
            navigate('/professional/dashboard', { replace: true });
            return;
          }
        }
      } catch (e) {
        console.error('Session check error:', e);
      } finally {
        setCheckingAuth(false);
      }
    }

    checkExistingSession();
  }, [supabase, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      toast.error('Supabase client unavailable');
      return;
    }

    const trimmed = identifier.trim();
    if (!trimmed || !password) {
      toast.error('Please enter your User ID or Email and password');
      return;
    }

    // Login technique: If input does not contain '@', append '@pro.bahibox.app'
    const authEmail = trimmed.includes('@')
      ? trimmed
      : `${trimmed.toLowerCase()}@pro.bahibox.app`;

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: password
      });

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new Error('Login failed: user profile not found');
      }

      toast.success('Signed in successfully!');
      navigate('/professional/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Professional login error:', err);
      // Surface exact backend error text (including invalid credentials or disabled/banned account)
      toast.error(err.message || 'Invalid login credentials');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 flex flex-col justify-between p-4 sm:p-6 text-slate-100">
      <div className="w-full max-w-md mx-auto my-auto py-8">
        {/* Brand / Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 rounded-2xl bg-primary/20 text-primary border border-primary/30 shadow-lg mb-3">
            <Wrench size={32} className="text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Professional Partner App
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Service job fulfillment, OTP verification & payments
          </p>
        </div>

        {/* Card */}
        <Card className="border border-slate-800 bg-slate-900/90 shadow-2xl rounded-2xl backdrop-blur-md">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  User ID ya Email
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="text"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder="e.g. BBP0012 ya name@email.com"
                    autoCapitalize="none"
                    autoCorrect="off"
                    required
                    className="pl-10 h-11 bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus:border-primary text-sm rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="pl-10 pr-10 h-11 bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus:border-primary text-sm rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl text-sm flex items-center justify-center gap-2 shadow-md transition-all mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In to Dashboard</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-slate-800/80 text-center">
              <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5">
                <ShieldCheck size={14} className="text-emerald-400" />
                <span>Credentials are issued by your business manager.</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a
            href="/login"
            className="text-xs text-slate-400 hover:text-white transition-colors"
          >
            Are you a merchant or store owner? <u>Sign in to Merchant Portal</u>
          </a>
        </div>
      </div>

      <footer className="text-center text-xs text-slate-500 py-2">
        BahiBox Daily Services &copy; {new Date().getFullYear()} &bull; Professional Field App
      </footer>
    </div>
  );
}
